import { useState } from "react";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import { api } from "@/lib/api";
import { trackChalkos as track } from "@/lib/analytics";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const field = "mt-1.5 w-full border-2 border-[#D3CFC0] bg-white px-3 py-3 text-[#111315] outline-none focus:border-[#DB9130]";

export function CheatSheetForm({ source = "cheat_sheet_page" }) {
  const [f, setF] = useState({ first_name: "", email: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const email = f.email.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) { setErr("Enter a valid email address."); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/tmhf/cheat-sheet", { ...f, email, first_name: f.first_name.trim() || null });
      track("tmhf_cheat_sheet_submit", { source });
      setDone(data);
    } catch (ex) {
      setErr(ex?.response?.status === 429 ? "Too many tries. Give it a few minutes." : "Something went wrong. Try again.");
    } finally { setBusy(false); }
  };

  if (done?.download_url) {
    return (
      <div className="border-2 border-[#111315] bg-white p-6" data-testid="cheat-sheet-thanks">
        <h3 className="font-head uppercase text-2xl text-[#111315]">It&apos;s on its way.</h3>
        <p className="mt-2 text-sm text-[#3A3A3A]">Download it now, and check your inbox for a copy.</p>
        <a href={done.download_url} data-testid="cheat-sheet-download-btn"
          className="mt-4 inline-flex items-center gap-2 bg-[#DB9130] text-[#111315] font-head uppercase tracking-wide px-6 py-3 hover:bg-[#111315] hover:text-[#F8F7F3] transition-colors">
          <Download size={16} /> Download the Cheat Sheet
        </a>
        <div className="mt-6 pt-5 border-t-2 border-[#D3CFC0]" data-testid="cheat-sheet-bundle-offer">
          <p className="text-sm text-[#111315]">Ready for the full picture? The <b>Teach Me How to Football Bundle</b>: the Beginner&apos;s Guide, Football IQ for Players and this Cheat Sheet for $24.</p>
          <Link to="/shop/tmhf_bundle" className="mt-3 inline-block font-head uppercase text-sm underline underline-offset-4 text-[#111315] hover:text-[#DB9130]" data-testid="cheat-sheet-bundle-link">See the bundle</Link>
        </div>
      </div>
    );
  }
  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid="cheat-sheet-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block"><span className="kicker text-[#6E6E6E]">First name (optional)</span>
          <input value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} maxLength={60} autoComplete="given-name" className={field} data-testid="cheat-sheet-first-name" />
        </label>
        <label className="block"><span className="kicker text-[#6E6E6E]">Email</span>
          <input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} autoComplete="email" className={field} data-testid="cheat-sheet-email" />
        </label>
      </div>
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={f.website}
        onChange={(e) => setF({ ...f, website: e.target.value })} className="absolute -left-[9999px] w-px h-px opacity-0" />
      {err && <p className="text-sm text-[#B3261E]" data-testid="cheat-sheet-error">{err}</p>}
      <button type="submit" disabled={busy} data-testid="cheat-sheet-submit"
        className="w-full sm:w-auto bg-[#DB9130] text-[#111315] font-head uppercase tracking-wide text-lg px-8 py-3.5 hover:bg-[#111315] hover:text-[#F8F7F3] transition-colors disabled:opacity-60">
        {busy ? "Sending..." : "Send me the Cheat Sheet"}
      </button>
      <p className="text-xs text-[#6E6E6E]">You&apos;ll also get The Sideline, the weekly VoluntoldHQ email. No spam. Unsubscribe anytime. <Link to="/legal/privacy" className="underline">Privacy</Link></p>
    </form>
  );
}
